import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useLocalStorage } from "../utils/useLocalStorage";
import {
  ANDROID_OS_REGEX,
  APP_BANNER_LOCAL_STORAGE_KEY,
  DAYS_APP_BANNER_SHOULD_BE_HIDDEN,
  MOBILE_REGEX,
  ONE_DAY_DURATION,
} from "./constants";

interface AppBannerLocalStorageValue {
  dismissedAt: string;
}

const DEFAULT_PROPERTIES = {
  isMobile: false,
  isMobileOsAndroid: false,
};

export const useAppBanner = () => {
  const { getValue: getLocalStorageValue } =
    useLocalStorage<AppBannerLocalStorageValue>(APP_BANNER_LOCAL_STORAGE_KEY);

  const [mobileProperties, setMobileProperties] = useState(DEFAULT_PROPERTIES);
  const [hasMetaAppId, setHasMetaAppId] = useState(false);
  const [hasTimeFrameDismiss, setHasTimeFrameDismiss] = useState(false);

  const location = useLocation();

  const isGettingStartedPage = location.pathname === "/getting-started";
  const isCurrentDeviceAndroidMobile =
    mobileProperties.isMobile && mobileProperties.isMobileOsAndroid;

  const shouldRenderAppBanner =
    hasMetaAppId &&
    hasTimeFrameDismiss &&
    isCurrentDeviceAndroidMobile &&
    !isGettingStartedPage;

  const checkHasAppId = useCallback(() => {
    const appId = document.querySelector('meta[name="google-play-app"]');
    appId ? setHasMetaAppId(true) : setHasMetaAppId(false);
  }, []);

  const checkIsAndroid = useCallback((userAgent: string) => {
    const isAndroid = ANDROID_OS_REGEX.test(userAgent);
    setMobileProperties(mobileProperties => ({
      ...mobileProperties,
      isMobileOsAndroid: isAndroid,
    }));
  }, []);

  const checkIsMobile = useCallback((userAgent: string) => {
    const isMobile = MOBILE_REGEX.test(userAgent);
    setMobileProperties(mobileProperties => ({
      ...mobileProperties,
      isMobile,
    }));
  }, []);

  const checkHasDismissed = useCallback(() => {
    const appBannerLocalStorageValue = getLocalStorageValue();

    if (appBannerLocalStorageValue) {
      const appBannerDismissedAt = new Date(
        appBannerLocalStorageValue.dismissedAt,
      );
      const now = new Date();
      const diffTime = Math.abs(now.getTime() - appBannerDismissedAt.getTime());
      const diffDays = Math.ceil(diffTime / ONE_DAY_DURATION);

      if (diffDays > DAYS_APP_BANNER_SHOULD_BE_HIDDEN) {
        setHasTimeFrameDismiss(true);
      }
    } else {
      setHasTimeFrameDismiss(true);
    }
  }, [getLocalStorageValue]);

  useEffect(() => {
    const userAgent = navigator.userAgent;

    checkHasAppId();
    checkIsMobile(userAgent);
    checkIsAndroid(userAgent);
    checkHasDismissed();
  }, [checkHasDismissed, checkHasAppId, checkIsMobile, checkIsAndroid]);

  return { shouldRenderAppBanner };
};
